import { apiClient, type ApiResponse } from './client';

export type RoutingStatus = 'ACTIVE' | 'DRAFT' | 'OBSOLETE';

export interface RoutingStage {
    sequence: number;
    workCenterId: string;
    workCenterName: string;
    machineId: string;
    machineName: string;
    operationName: string;
    setupTimeMinutes: number;
    runTimeMinutes: number;
    description?: string;
}

export interface RoutingPayload {
    id?: string;
    name: string;
    code: string;
    itemId?: string | null;
    itemName?: string | null;
    itemIds?: string[];
    itemNames?: string[];
    applicableItems?: any[];
    version?: string;
    status: RoutingStatus;
    stages: RoutingStage[];
    totalTimeMinutes?: number;
    isActive?: boolean;
    warehouseId?: string | null;
}

export interface RoutingResponse {
    id: string;
    name: string;
    code: string;
    item_id: string | null;
    item_name: string | null;
    item_ids?: string[] | null;
    applicable_items?: any[] | null;
    version: string;
    status: RoutingStatus;
    stages: RoutingStage[] | string;
    total_time_minutes: number;
    is_active: boolean;
    created_at: string;
    updated_at: string;
    warehouse_id?: string | null;
}

export interface RoutingDropdownItem {
    id: string;
    name: string;
    code: string;
    status: string;
    stages?: any[] | string;
    total_time_minutes?: number;
    item_id?: string | string[] | null;
    item_ids?: string[] | null;
    item_name?: string | null;
    warehouse_id?: string | null;
}


const BASE = '/api/v1/routings';

export async function createRouting(
    payload: RoutingPayload,
): Promise<ApiResponse<RoutingResponse>> {
    const formattedPayload = {
        id: payload.id,
        name: payload.name,
        code: payload.code,
        item_id: payload.itemId,
        item_name: payload.itemName,
        item_ids: payload.itemIds,
        item_names: payload.itemNames,
        applicable_items: payload.applicableItems,
        version: payload.version,
        status: payload.status,
        stages: payload.stages,
        total_time_minutes: payload.totalTimeMinutes,
        is_active: payload.isActive,
        warehouse_id: payload.warehouseId || null,
    };
    const { data } = await apiClient.post<ApiResponse<RoutingResponse>>(
        `${BASE}/create`,
        formattedPayload,
    );
    return data;
}

export async function getAllRoutings(warehouseId?: string): Promise<
    ApiResponse<RoutingResponse[]>
> {
    const query = warehouseId && warehouseId !== 'ALL' ? `?warehouse_id=${encodeURIComponent(warehouseId)}` : '';
    const { data } = await apiClient.get<ApiResponse<RoutingResponse[]>>(
        `${BASE}/${query}`,
    );
    return data;
}

export async function getRoutingById(id: string): Promise<ApiResponse<any>> {
    const { data } = await apiClient.get<ApiResponse<any>>(`${BASE}/${id}`);
    return data;
}

export async function updateRouting(
    id: string,
    payload: Partial<RoutingPayload>,
): Promise<ApiResponse<RoutingResponse>> {
    const formattedPayload = {
        name: payload.name,
        code: payload.code,
        item_id: payload.itemId,
        item_name: payload.itemName,
        item_ids: payload.itemIds,
        item_names: payload.itemNames,
        applicable_items: payload.applicableItems,
        version: payload.version,
        status: payload.status,
        stages: payload.stages,
        total_time_minutes: payload.totalTimeMinutes,
        is_active: payload.isActive,
        warehouse_id:
            payload.warehouseId !== undefined
                ? payload.warehouseId || null
                : undefined,
    };
    const { data } = await apiClient.put<ApiResponse<RoutingResponse>>(
        `${BASE}/${id}`,
        formattedPayload,
    );
    return data;
}

export async function deleteRouting(id: string): Promise<ApiResponse<null>> {
    const { data } = await apiClient.delete<ApiResponse<null>>(`${BASE}/${id}`);
    return data;
}

// ✅ NEW: Get routings for dropdown
// Accepts a warehouse id, or a filter object (status / item / warehouse)
export async function getRoutingsForDropdown(arg?: string | {
    status?: string;
    itemId?: string;
    item_id?: string;
    warehouseId?: string;
    warehouse_id?: string;
}): Promise<RoutingDropdownItem[]> {
    const params: Record<string, string> = {};
    if (typeof arg === 'string') {
        if (arg && arg !== 'ALL') params.warehouse_id = arg;
    } else if (arg) {
        Object.entries(arg).forEach(([k, v]) => {
            if (v && v !== 'ALL') params[k] = v;
        });
    }
    const { data } = await apiClient.get<ApiResponse<RoutingDropdownItem[]>>(
        `${BASE}/dropdown`,
        { params },
    );
    return data.data || [];
}

// ✅ Stage-wise BOM Materials APIs
export async function getStageMaterials(stageId: string): Promise<ApiResponse<any[]>> {
    const { data } = await apiClient.get<ApiResponse<any[]>>(`${BASE}/stages/${stageId}/materials`);
    return data;
}

export async function saveStageMaterials(
    stageId: string,
    payload: { bom_component_ids?: string[]; item_ids?: string[]; materials?: any[] }
): Promise<ApiResponse<{ status: 'ACTIVE' | 'DRAFT'; mapped_count: number }>> {
    const { data } = await apiClient.post<ApiResponse<{ status: 'ACTIVE' | 'DRAFT'; mapped_count: number }>>(
        `${BASE}/stages/${stageId}/materials`,
        payload
    );
    return data;
}
